import { FC, useEffect, useMemo } from 'react';
import Page, { PageProps } from '@/components/page';
import RouterTabs from '@/components/router-tabs';
import ProfileForm from './profile-form';
import { useQuery } from 'react-query';
import { Navigate, useParams } from '@tanstack/react-router';
import { getPolicyV1ById } from '@/lib/services/policy-v1.service';
import useApp from '@/hooks/use-app.hook';
import IntentsForm from './intents-form';
import { QueryKey } from '@/lib/query-client';
import ServiceAccessForm from './service-access-form';
import GroupPolicySensitiveDataForm from './sensitive-data-form';
import PortalForm from './portal-form';
import McpAccessForm from './mcp-access-form';
import useFeature from '@/hooks/use-feature.hook';

const PolicyViewPage: FC<PageProps> = () => {
  const { policyId } = useParams({ from: '/wais/group-policies/$policyId' });
  const { setPageTitle } = useApp();
  const { hasPolicyV2 } = useFeature();

  const { data: policy } = useQuery([QueryKey.PolicyView, policyId], async () =>
    getPolicyV1ById(policyId)
  );

  useEffect(() => {
    if (policy) {
      setPageTitle(policy.name, 'policy');
    }
  }, [policy, setPageTitle]);

  const panels = useMemo(() => {
    const panels = [
      {
        label: 'Profile',
        path: 'profile',
        component: <ProfileForm policyId={policyId} />,
      },
      {
        label: 'Services',
        path: 'service-access',
        component: <ServiceAccessForm policyId={policyId} />,
      },
      {
        label: 'MCP Tools',
        path: 'mcp-tools',
        component: <McpAccessForm policyId={policyId} />,
      },
      {
        label: 'Sensitive Data',
        path: 'sensitive-data',
        component: <GroupPolicySensitiveDataForm policyId={policyId} />,
      },
      {
        label: 'Classifiers',
        path: 'classifiers',
        component: <IntentsForm policyId={policyId} />,
      },
      {
        label: 'Portal Chat',
        path: 'portal',
        component: <PortalForm policyId={policyId} />,
      },
    ];

    return panels;
  }, [policyId]);

  if (hasPolicyV2) {
    return <Navigate to="/config/policy/scoped" />;
  }

  return (
    <Page>
      <RouterTabs panels={panels} />
    </Page>
  );
};

export default PolicyViewPage;
